import * as _ from 'radashi'

import type { Template } from '#library/types.ts'
import type { Link } from 'storyloco/link'
import { links_map } from '#library/links.build.ts'
import { error, isHttpError, isRedirect, redirect } from '@sveltejs/kit'
import chalk from 'chalk'
import { browser } from '$app/env'
import { Storyblok } from '#library/storyblok.svelte.ts'
import { findObject } from 'kitto'
import { get_sibling_links } from '#library/siblings.ts'

// Pages are cached at the CDN via the cache-control set in +layout.server.ts,
// which keys on the full URL and so handles filters, search and pagination.

const prospectus = 'https://prospectus.bedales.org.uk'

export async function load(event) {
	const {
		parent,
		params: { page }
	} = event
	const { api, version } = await parent()
	const slug = page || 'home'

	try {
		const response = await api.get(`cdn/stories/${slug}`, {
			version,
			resolve_links: 'url',
			resolve_relations: Storyblok.relations
		})

		const story = response.data.story as Template
		// the prospectus brochure shares this space but isn't a page here: it's its own site, which the
		// visual editor's preview is sent on to, query and all, so it connects there
		if ((story.content.component as string) === 'brochure') {
			if (!event.url.searchParams.has('_storyblok')) error(404, 'Not found')
			redirect(307, `${prospectus}/${event.url.search}`, { external: [prospectus] })
		}
		const navblock = findObject(story?.content?.blocks ?? [], 'component', 'navblock')
		if (navblock?.root?.cached_url) {
			navblock.links = await get_sibling_links(api, navblock.root?.cached_url)
		}

		// rewrite any link components to use our canonical paths from links_map
		function rewrite_links(value: unknown): void {
			if (Array.isArray(value)) {
				for (const item of value) rewrite_links(item)
				return
			}

			if (value && typeof value === 'object') {
				const obj = value as Record<string, unknown>

				if (obj.component === 'link' && obj.link && typeof obj.link === 'object') {
					const link = obj.link as Partial<Link>
					if (link.type === 'internal' && link.story) {
						const suffix = typeof link.suffix === 'string' ? link.suffix : ''
						if (typeof link.story.id === 'number') {
							const story_id = link.story.id
							if (links_map.has(story_id)) {
								const { pathname } = links_map.get(story_id)!
								link.url = `${pathname}${suffix}`
							}
						} else if (typeof link.url === 'string') {
							console.warn('link does not have a story id', link)
							link.url = `${link.url}${suffix}`
						}
					}
				}

				for (const key of Object.keys(obj)) rewrite_links(obj[key])
			}
		}

		rewrite_links(story.content)

		/* Load template and block data.
		 *
		 * These ran one after another, so a page's data cost was the sum of every
		 * block's round trips rather than the slowest one. Each load writes only to
		 * its own bloks (or to story.content for a template), so they don't contend
		 * and can run together. One failing load still can't take the page down.
		 */
		let template: string = story.content.component || 'page'
		if (template === 'news') template = 'blog' // News uses the blog template

		// a block's load handles every instance of its component, so run one per
		// distinct component rather than once per block on the page
		const components =
			'blocks' in story.content && story.content.blocks
				? [
						...new Set(
							story.content.blocks
								.map(block => block.component as string | undefined)
								.filter(
									(component): component is string => !!component && !!Storyblok.blocks.get(component)
								)
						)
					]
				: []

		async function run(module: Promise<unknown>) {
			try {
				const { load } = (await module) as { load?: Load }
				if (load) await load({ api, version, event, story })
			} catch (error) {
				console.error(error)
			}
		}

		await Promise.all([
			run(import(`../../templates/${template}.svelte`)),
			...components.map(component => run(import(`../../blocks/${component}.svelte`)))
		])

		return { story }
	} catch (_error) {
		if (isRedirect(_error) || isHttpError(_error)) throw _error
		if (!browser) {
			console.group(chalk.red(`Error from ${chalk.white('page.ts')}`))
			console.error(`Unable to load ${chalk.yellow(slug)}`)
			const ignore = ['.well-known/']
			if (!ignore.some(i => slug.startsWith(i))) console.log(_error)
			console.groupEnd()
		}
		return error(404, 'Not found')
	}
}
